/* app/ai-drawer.jsx - the topbar AI assistance trigger and its side panel.

   One icon in the topbar, on every screen, opening an assistant ALREADY
   POINTED AT THE MODULE IT WAS OPENED ON. That is the point of it: the
   Site > AI Assistant screen is a place you navigate to and then have to
   explain yourself to ("I am on the Keywords Planner, there is a Classify
   button, what does it do"), a tax paid on every question. Opened from the
   topbar, where the user is already in the prompt.

   THE PANEL IS A COLUMN, NOT A MODAL. It docks beside the screen and the
   screen keeps working: no scrim, nothing to dismiss, the table underneath
   stays clickable and reflows rather than hiding. An assistant you must close
   to do the thing it just told you to do is a worse assistant. It closes by
   itself when the user moves to another screen, because the conversation was
   about the screen they left.

   THE JOB, NOT THE MANUAL. The panel is handed the module AND the site's
   actual state (GET /assistant/context: the business profile, both banks, the
   plans, the BI registers, the pipeline, the last audit and 28 days of Search
   Console, including the queries sitting just off the first page), plus a map
   of every module in the app. So "what should I do next" is answered with this
   site's work in the right order, naming the module that does each step, and
   the answer is allowed to point anywhere in the app rather than only at the
   screen it was opened on. The figures are facts it must use and must not
   invent beyond.

   One conversation per module, stored, so a chat is never lost: see
   routes/assistant.js. The trigger carries a dot while the module it would
   open has a saved chat.

   The Site > AI Assistant screen (pipeline.jsx Assistant) is deliberately left
   alone: it is the site-wide strategy chat and keeps its own sidebar entry.

   Attached to window like every other module; the shell reaches it at render
   time as window.MlAiDrawer. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useRef, useEffect } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;

  /* What each module lets you DO, in the module's own terms, plus three
     openers worth a click. SCREEN_META already says what a module is for, and
     that line is sent as well, so these say the part a one-line subtitle
     cannot: which controls are on the screen and what they produce. Kept
     high-level on purpose, a brief that lists a button that later moves would
     teach the assistant to send people looking for it. */
  const MODULE_CTX = {
    // The console's property screen. The only entry here that is not an app
    // module, because the question it exists for is about the WEBSITE rather
    // than about one site's content: how a property should be arranged.
    //
    // Be careful what this promises. Sections and roles are not modelled
    // (docs/PROPERTY-MODEL.md 2b), markets scope nothing yet, and a blog is
    // recognised by its address rather than declared. The assistant can read
    // the structure and reason about it, so the questions ask for judgment,
    // not for changes it cannot make.
    "property-structure": {
      // SCREEN_META covers the app's nav screens and has no entry for a
      // console screen, so the label is given here or the panel would open
      // titled "MarketLens".
      label: "Property structure",
      purpose: "How this whole website is put together.",
      does: "Look at how this whole website is put together: its markets, the sites connected to it, which parts can be published to and which are read by crawling, and how many pages each holds. Judgment about structure, not changes: sections and roles are not modelled yet, so nothing here can create a blog or move pages between markets.",
      asks: [
        "Is this website structured well for search?",
        "What is missing from this property?",
        "Should these markets be arranged differently?",
      ],
    },
    // Shipped with crawl-only sites and never given an entry, so the one
    // screen a site with no CMS actually lives on opened the assistant with
    // generic starter questions.
    "page-inventory": {
      does: "Every page this site serves, as the crawler last saw it: path, title, meta description, canonical, h1, language, word count, noindex and HTTP status. Refresh re-crawls from the sitemap, and Export hands the list over as a file, which is how a change reaches a site with no CMS to save into.",
      asks: [
        "Which pages here need their titles or descriptions fixed?",
        "Why do these pages show no words and no h1?",
        "What should I hand my developer first?",
      ],
    },
    posts: {
      does: "List, filter and sort the site's WordPress posts; open one in the editor; publish a draft; move one to trash; or generate a new article from a focus keyword.",
      asks: ["What can I do from this list?", "How do I publish a draft?", "How do I decide what to write next?"],
    },
    pages: {
      does: "The same list for WordPress pages: filter, sort, open in the editor, publish or trash. Pages carry no categories or tags.",
      asks: ["What can I do from this list?", "How do pages differ from posts here?", "How do I fix a page's SEO meta?"],
    },
    categories: {
      does: "Manage the site's category taxonomy: create a term (name, slug, parent, description), rename one in place, re-file it under another parent, or delete it. The list is in tree order.",
      asks: ["How should I structure my categories?", "What happens to posts if I rename a category?", "How many categories should a site have?"],
    },
    tags: {
      does: "Manage the site's tags: create, rename in place, or delete. Tags are flat, with no parent.",
      asks: ["When should something be a tag instead of a category?", "How do I clean up near-duplicate tags?", "How many tags should one post carry?"],
    },
    "taxonomy-suggestions": {
      does: "AI proposes new categories and tags from chosen sources (posts, pages, the keyword bank, the topics bank). Suggestions move draft, approved (the term is created in WordPress), in use. Consolidate finds duplicates and terms filed as the wrong kind.",
      asks: ["Which sources should I generate from?", "What does approving a suggestion actually do?", "What does Consolidate look for?"],
    },
    "taxonomy-audit": {
      does: "Scan published posts and suggest which existing or approved terms each one is missing, then apply per row or all at once. The write-back is additive and never removes a term a post already has.",
      asks: ["What does this scan look at?", "Is applying safe for my live posts?", "Why did a post get no suggestions?"],
    },
    reports: {
      does: "A read-only assembled report for the site: search performance over the last 28 days, the content pipeline by status, SEO health from the last audit, tracked competitors and open to-dos. Export is the browser's print dialog.",
      asks: ["How do I read this report?", "Which numbers here should worry me?", "Why is a section of this report empty?"],
    },
    "seo-dashboard": {
      does: "Site-wide SEO health at a glance, with the way into Performance, Site Audit, Bulk Meta Editor and Internal Linking.",
      asks: ["Where should I start with SEO on this site?", "What does this dashboard pull from?", "What should I fix first?"],
    },
    performance: {
      does: "Google Search Console data for the site: clicks, impressions, CTR and average position over time, by query and page. Needs the site connected to Google.",
      asks: ["Which queries am I closest to winning?", "What does a falling CTR at a steady position mean?", "How do I connect Search Console?"],
    },
    "site-audit": {
      does: "Crawl the site and group technical and on-page issues by severity, in two lenses: the REST/database view and the public-HTML crawl (so headless sites are still audited).",
      asks: ["Which of these issues matter most?", "Why do the two lenses disagree?", "How often should I re-run this?"],
    },
    "bulk-meta": {
      does: "Inline-edit title tags and meta descriptions across many URLs at once, with length guidance, and save back to the posts' SEO meta.",
      asks: ["How long should a title tag and meta description be?", "How do I rewrite these quickly?", "Does saving here publish to the live page?"],
    },
    "internal-linking": {
      does: "AI-suggested internal links between the site's own content, including which pages are orphans with nothing pointing at them.",
      asks: ["How many internal links should a post have?", "What makes a good anchor text?", "Which link suggestions should I skip?"],
    },
    "content-planner": {
      does: "Plans are bodies of work; each plan holds pieces, and a piece becomes one published post. Add keywords to a plan, let it propose pieces, brief a piece, then generate and send the draft to the editor.",
      asks: ["How do I turn keywords into a plan?", "What makes a good brief for a piece?", "How do I get a piece to a draft?"],
    },
    "keywords-planner": {
      does: "The site's keyword register: import or add keywords, classify them with AI for intent and priority, group them into clusters, select in bulk, and push a selection into a content plan.",
      asks: ["What does classifying a keyword decide?", "How do clusters get built?", "How do I pick the keywords worth writing about?"],
    },
    "topics-bank": {
      does: "The site's topic register, mined from research: every topic captured, published or not, with its state, ready to be sent to a content plan or drafted.",
      asks: ["Where do these topics come from?", "How do I turn a topic into a post?", "Why is a topic sitting unclassified?"],
    },
    "coverage-map": {
      does: "Shows which categories and tags have content behind them and which are only names, so an empty branch of the taxonomy is visible.",
      asks: ["What does an empty term here mean?", "Which gap should I fill first?", "Should I delete terms with no content?"],
    },
    "industry-analysis": {
      does: "Run an AI analysis lens against an industry entity from Business Intelligence, then capture findings from the result into keywords, topics or to-dos.",
      asks: ["What does this lens produce?", "What should I capture from a result?", "How is this different from the competitor lens?"],
    },
    "competitors-analysis": {
      does: "Run an AI analysis against a tracked competitor: gaps versus us, keyword overlap and content opportunities, read from their whole site rather than one page. Findings can be captured into keywords, topics or to-dos.",
      asks: ["How do I read a gaps-vs-us result?", "What should I capture from this analysis?", "Why is part of an answer marked incomplete?"],
    },
    "partners-analysis": {
      does: "Run an AI analysis against a partner or complementary entity: shared topics and co-marketing angles, with findings capturable into keywords, topics or to-dos.",
      asks: ["What does this lens look for?", "How do I use a co-marketing angle?", "What should I capture from this?"],
    },
    "product-enrichment": {
      does: "Enrich the site's product offerings with AI-gathered detail, so the products register carries more than a name.",
      asks: ["What does enrichment add to a product?", "How do I review what it found?", "Why would a product come back thin?"],
    },
    inbox: {
      does: "Capture research: paste text or fetch a URL, keep it as an item, then summarise an item into a draft or send it on into the pipeline.",
      asks: ["What is worth capturing here?", "How do I turn an item into a draft?", "What happens to an item once I act on it?"],
    },
    todo: {
      does: "Research and content tasks across their states, gathered from the modules that raise them, with the way back to the thing each task is about.",
      asks: ["Where do these to-dos come from?", "Which of these should I do first?", "How do I close a to-do properly?"],
    },
    "business-profile": {
      does: "The owner entity: who this business is, what it sells, its market constraint and its writing language. Everything AI generates for the site is grounded in this, so it is the first thing to fill in.",
      asks: ["Why does this profile matter so much?", "What does the market constraint change?", "How does the writing language get used?"],
    },
    products: {
      does: "The site's offerings: products with their providers, coverage gaps and nested variants, each one an entity other modules can analyse.",
      asks: ["How should I model a product with variants?", "What is a coverage gap here?", "How do products feed content planning?"],
    },
    "bi-competitors": {
      does: "The tracked competitor entities: add one, record what it is and how it relates to us (lookalike, alternative, complementary), and open its analysis.",
      asks: ["Who counts as a competitor worth tracking?", "What do the relationship labels mean?", "How many competitors should I track?"],
    },
    "bi-industry": {
      does: "The industry entities and segments the site operates in, each one an entity the industry analysis lens can run against.",
      asks: ["What belongs in this register?", "How specific should a segment be?", "How does this feed research?"],
    },
    "bi-partners": {
      does: "The partner and complementary entities: who you sit alongside rather than compete with, ready for the partners analysis lens.",
      asks: ["What makes something a partner and not a competitor?", "What do I get from analysing a partner?", "Who should I add here?"],
    },
    "ai-assistant": {
      does: "The site-wide strategy chat: content and SEO guidance for the whole site rather than one screen.",
      asks: ["What should I ask here instead of in a module?", "Summarise what to focus on this month", "Where am I losing ground?"],
    },
    "editor-post": {
      label: "Post editor",
      purpose: "Write a post and set everything search engines read about it.",
      does: "Write and save a post (draft first, publish explicitly), set its SEO fields (focus keyword, up to three secondaries, slug, title tag, meta description, OG, canonical, robots, schema), and run AI Fill SEO or Analyze content.",
      asks: ["How do I improve this post's SEO fields?", "Why only three secondary keywords?", "What does Analyze content check?"],
    },
    "editor-page": {
      label: "Page editor",
      purpose: "Write a page and set everything search engines read about it.",
      does: "Write and save a page (draft first, publish explicitly) and set its SEO fields, including title tag, meta description, canonical, robots and schema.",
      asks: ["What should a page's meta description say?", "How do I set a canonical URL?", "When should a page be noindex?"],
    },
  };

  /* A URL in an answer has to be CLICKABLE.
     The assistant can now hand over files (the Site Audit handoff is a real
     download at a real URL), and before this the address arrived as plain text
     in a bubble: the user asked "ok where to download now?" and the honest
     answer was that there was nothing to click. Only http(s) is linked and
     nothing else about the text is touched, because this is a bubble, not a
     markdown renderer. The trailing-character class keeps a sentence full stop
     out of the href. */
  const URL_IN_TEXT = /(https?:\/\/[^\s<>()\[\]]*[^\s<>()\[\].,;:!?'"])/g;
  // A model writing a link writes markdown, every time. Rendering the pair as
  // an anchor keeps the brackets and the raw URL out of the bubble, and it is
  // the only markdown this understands: the rest of the text is left alone.
  const MD_LINK = /\[([^\]]{1,120})\]\((https?:\/\/[^\s)]+)\)/g;
  function Linked({ text }) {
    const out = [];
    let rest = String(text == null ? "" : text);
    let key = 0;
    // Markdown pairs first, so their URL is not linkified twice.
    let at = 0;
    for (const m of rest.matchAll(MD_LINK)) {
      if (m.index > at) out.push(...plain(rest.slice(at, m.index), () => key++));
      const file = downloadNameOf(m[2]);
      out.push(file
        ? <FileButton key={`md${key++}`} href={m[2]} name={file} />
        : <a key={`md${key++}`} className="ml-bubble__link" href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>);
      at = m.index + m[0].length;
    }
    out.push(...plain(rest.slice(at), () => key++));
    return out;
  }
  /* A FILE the assistant handed over is a button, not an address.
     The handoff URL is 200 characters of query string, and pasting that into
     a bubble asks the user to read a URL to find out what it is. Anything
     carrying download=1 renders as one control saying the filename, which is
     what they were promised when they asked for a PDF. Every other URL stays
     an inline link: a reference to a page is not a download. */
  function downloadNameOf(href) {
    try {
      const u = new URL(href, window.location.origin);
      if (u.searchParams.get("download") !== "1") return null;
      const named = u.searchParams.get("filename");
      if (named) return named.split("/").pop();
      const fmt = (u.searchParams.get("format") || "").toUpperCase();
      return fmt ? `the ${fmt} file` : "the file";
    } catch { return null; }
  }
  function FileButton({ href, name }) {
    // The design system's own primary button, so a file the assistant hands
    // over looks like every other thing you press in this app rather than
    // like a chat affordance. `download` only when the name is a real
    // filename: on a bare format it would save the file as "the PDF file".
    return (
      <a className="hds-btn hds-btn--primary ml-dl" href={href}
         download={name.includes(".") ? name : undefined} title={name}>
        <MlIcon name="download" size={15} />
        <span className="ml-dl__name">{name}</span>
      </a>
    );
  }

  function plain(text, nextKey) {
    // split() with one capture group puts the matches at the odd indexes.
    return String(text).split(URL_IN_TEXT).map((part, i) => {
      if (i % 2 === 0) return part;
      const file = downloadNameOf(part);
      return file
        ? <FileButton key={`u${nextKey()}`} href={part} name={file} />
        : <a key={`u${nextKey()}`} className="ml-bubble__link" href={part} target="_blank" rel="noopener noreferrer">{part}</a>;
    });
  }

  // Title and purpose come from the same place the screen header reads, so the
  // assistant can never name the module something the user is not looking at.
  function describe(moduleId) {
    const meta = (window.MlData && window.MlData.SCREEN_META) || {};
    const ctx = MODULE_CTX[moduleId] || {};
    const m = meta[moduleId] || {};
    return {
      id: moduleId || "app",
      label: ctx.label || m.title || "MarketLens",
      // SCREEN_META covers the nav screens; anything else (the editor) says
      // its own purpose here rather than opening with a bare title.
      purpose: m.sub || ctx.purpose || "",
      does: ctx.does || "",
      asks: ctx.asks || ["What is this screen for?", "What should I do here first?", "What does this feed into?"],
    };
  }

  // Every module as one line, so a suggestion can send the user anywhere in
  // the app by its real name. The open module is left out of this list: it
  // gets the full brief above instead, and naming it twice invites the model
  // to describe it rather than use it.
  function appMap(exceptId) {
    const meta = (window.MlData && window.MlData.SCREEN_META) || {};
    return Object.keys(MODULE_CTX)
      // `property-structure` is a CONSOLE screen, not an app module. Listing
      // it here would invite the model to send someone to a screen the app
      // router cannot reach, which reads as the assistant inventing places.
      .filter(id => id !== exceptId && !id.startsWith("editor-") && id !== "property-structure")
      .map(id => {
        const m = meta[id] || {};
        const ctx = MODULE_CTX[id] || {};
        const what = (m.sub || ctx.does || "").split(". ")[0];
        return `- ${ctx.label || m.title || id}: ${what}`;
      })
      .join("\n");
  }

  function systemPrompt(mod, site, context) {
    const siteLine = site ? `${site.name} (${site.url || "no URL on record"})` : "the active site";
    return [
      `You are the MarketLens assistant, working inside the app on ${siteLine}. MarketLens manages WordPress sites: content and taxonomy, SEO and Search Console performance, keyword and topic research, content plans, and a business-intelligence register of competitors, partners, industries and products.`,
      "",
      `WHERE THE USER IS: ${mod.label}`,
      mod.purpose ? `What this screen is for: ${mod.purpose}` : "",
      mod.does ? `What can be done here: ${mod.does}` : "",
      "",
      "THE REST OF THE APP, by the names the user sees:",
      appMap(mod.id),
      "",
      context ? "THE SITE RIGHT NOW:" : "",
      context || "",
      "",
      "HOW TO ANSWER",
      "1. Do the job, do not narrate the screen. Say what to do next, in the order it should happen, and name the module and the control that does each step.",
      "2. Start where the user is, but think across the whole app: when the better next move lives in another module, say which one and why. A step in the wrong module is worse than a step they have to walk to.",
      "3. The figures above are facts about this site: use them, and prefer the specific over the general (\"your 14 unclassified keywords\" beats \"your keywords\"). Never invent a number, a competitor, a keyword, a plan or a post that is not in them.",
      "4. When something you would need is missing from the picture, say so and name the control that would produce it (for example: run a Site Audit, connect Search Console, fill in the Business Profile). Missing data is itself a next step, not an excuse.",
      "5. Short and concrete: a few sentences, or a short ordered list. No preamble, no restating the question, no sign-off.",
      "6. Write in the user's language. If the site has a content language set, drafts and titles you suggest go in that language.",
    ].filter(x => x !== "").join("\n");
  }

  /**
   * What to put in the bubble when a question fails.
   *
   * A 400 from our own route is an ANSWER: "no Anthropic key, add one in the
   * Account console" is the next step, and prefixing it with "I could not
   * reach the assistant" both buries it and says something untrue, since the
   * route answered immediately. Only a transport failure gets the reach line.
   */
  /* A GATEWAY failure is not an answer, so it is not shown as one.
     Live, on the first message of a session, this came back "I could not
     reach the assistant. Request failed (502)" and the turn was lost; the
     same question sent again a moment later worked. The server in front of
     the app had not finished starting. A person reading a status code and
     deciding to type it all again is us handing them our problem, so a
     transport failure is retried once, after a short wait, and only a second
     failure is shown. A 400 or a 403 is NEVER retried: those are answers.

     Retried ONCE and no more: the turn may have reached the model and spent
     real money before the gateway gave up, so a loop here would pay for the
     same work several times over. */
  const GATEWAY = [502, 503, 504];
  /* WHEN A RETRY IS SAFE, which is narrower than it first looked.
     The first version of this retried every gateway failure once, which was
     right for the case it was written for (the app had not finished starting
     and the request never arrived) and dangerous for the case that turned up
     next: a turn that classified 30 keywords ran for long enough that the
     gateway gave up, the server finished the work anyway, the register showed
     all 30 judged, and the user was told the request failed. Re-sending that
     turn spends the AI budget again and does the work twice.

     The client cannot see whether the server finished. It CAN see whether the
     turn was able to change anything: with Ask first on and no approvals in
     the body, every write pauses for a card before it runs, so the turn can
     only have performed reads and re-sending it costs nothing but time. Any
     turn carrying approvals, or running autonomously, may already have
     written, and those are never retried.

     That rule now covers the START of a run and nothing else. Starting is a
     short request that hands back an id, so a gateway failure on it really is
     the old case: the app was not up and the turn never arrived. Once an id
     exists there is nothing to decide, because the answer is on the server
     and the only sane move is to keep asking for it. */
  const mayHaveWritten = (body) => !!(body.autonomous || (body.approvals && Object.keys(body.approvals).length));

  /* A TURN IS A RUN NOW: started, then asked after, because a long turn and a
     gateway timer cannot both win.
     Posting the whole turn and holding the connection open was fine until a
     turn did real work. Measured live: classifying 30 keywords ran long enough
     that the server in front of the app gave up and answered 502. The turn
     itself completed, the register showed all 30 judged, and the user was told
     the request had failed, with re-sending it the obvious thing to try next,
     which would have done the work twice and spent the AI budget twice.
     No amount of care on a dead connection gets that answer back, so the post
     only starts the work and returns an id, and the client polls. Every
     request is short from here on, and no gateway timer is ever left waiting
     on the model. */
  const POLL_EVERY  = 1200;
  /* The answer is already sitting on the server and a poll is the cheap part,
     so a few failed checks are a wobble to sit through rather than a reason to
     throw away a turn that is very likely finishing. Ten in a row is not a
     wobble. */
  const POLL_MISSES = 10;
  // A ceiling, not an expected case: a run wedged on the server must not leave
  // the panel polling for the rest of the day.
  const RUN_CEILING = 10 * 60 * 1000;

  /* WHAT TO SAY WHEN WE STOP WAITING, and it is the same thing in all three
     cases (the run is gone, the checks keep failing, the ceiling is reached):
     we do not know whether it finished, and the server most likely did.
     Calling that a failure invites the one move that makes it worse, so the
     sentence names the uncertainty and sends the user to look. `said` marks a
     message that is already the whole answer, so failureText prints it as it
     is instead of prefixing a reach line that would not be true. */
  const CHECK_FIRST = "The work may still have gone through: check before asking for it again, because repeating it would do it twice.";
  const stopWaiting = (text) => { const e = new Error(text); e.said = true; return e; };

  const startRun = (body) => api("/assistant/act", { method: "POST", body: JSON.stringify({ ...body, async: true }) });

  async function actOnce(body, onPerformed) {
    let begun;
    try {
      begun = await startRun(body);
    } catch (e) {
      if (!e || !GATEWAY.includes(e.status)) throw e;
      if (mayHaveWritten(body)) { e.mayHaveWritten = true; throw e; }
      await new Promise(r => setTimeout(r, 1500));
      begun = await startRun(body);
    }
    const runId = begun && begun.run_id;
    // No id back means this is a server without the run routes, which is every
    // request in the minute a deploy takes. It answered the old way, so what is
    // already in hand IS the result.
    if (!runId) return begun;

    const until = Date.now() + RUN_CEILING;
    let misses = 0;
    for (;;) {
      await new Promise(r => setTimeout(r, POLL_EVERY));
      let state;
      try {
        state = await api(`/assistant/act/${encodeURIComponent(runId)}`);
      } catch (e) {
        // The run is not there any more: it expired, or the app restarted under
        // it. Terminal, because no further poll can produce an answer, and the
        // work may well have landed before the record of it went.
        if (e && e.status === 404) throw stopWaiting("I lost track of this turn, so I cannot show you what it said. " + CHECK_FIRST);
        // A gateway wobble, or no status at all because the connection itself
        // dropped, is the cheap half failing and not the turn failing, so it is
        // sat through. Anything else is an answer and is passed on.
        const st = e && e.status;
        if (st && !GATEWAY.includes(st)) throw e;
        if (++misses >= POLL_MISSES) throw stopWaiting("I could not check on this turn, several times over. " + CHECK_FIRST);
        continue;
      }
      misses = 0;
      if (state && state.status === "done") return state.result;
      // The server's own sentence about why it stopped is the answer, so it is
      // shown as written.
      if (state && state.status === "failed") throw stopWaiting(state.error || "That turn stopped before it finished.");
      // Still running: what it has done so far, so the trail under the answer
      // fills in while the rest of the work happens.
      if (onPerformed && state && Array.isArray(state.performed)) onPerformed(state.performed);
      if (Date.now() > until) throw stopWaiting("This turn has been running for ten minutes, so I have stopped waiting on it. " + CHECK_FIRST);
    }
  }

  function failureText(e) {
    const msg = (e && e.message) || "";
    // Already a whole sentence written for a person to read: see stopWaiting.
    if (e && e.said && msg) return msg;
    if (e && (e.status === 400 || e.status === 403) && msg) return msg;
    if (e && GATEWAY.includes(e.status)) {
      // "Nothing was changed" was a guess, and it was wrong: the work had
      // already landed. The honest answer names the uncertainty and says what
      // to do about it, because sending an expensive turn again on the chance
      // it failed is the one move that makes this worse.
      return e.mayHaveWritten
        ? "The app took too long to answer, so I lost this turn. The work may still have gone through: "
          + "check before asking for it again, because repeating it would do it twice."
        : "The app did not answer, twice. Nothing was changed, so you can send that again.";
    }
    return "I could not reach the assistant. " + msg;
  }

  // The first text block of the reply. Thinking is disabled server-side for
  // this route, but reading content[0] blind is how a thinking block ends up
  // rendered as the answer the day that changes.
  function replyText(res) {
    const blocks = (res && res.content) || [];
    const t = blocks.find(b => b && b.type === "text" && b.text);
    return (t && t.text) || "(no response)";
  }

  // The site id the chats are filed under, in the two shapes the shell's site
  // object comes in (the console passes site_id, the app shell id).
  const siteIdOf = (site) => (site ? (site.site_id != null ? site.site_id : site.id) : null);

  // "4 min ago" / "yesterday": a history list is read by when, not by date.
  function ago(iso) {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return mins + " min ago";
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return hrs + (hrs === 1 ? " hour ago" : " hours ago");
    const days = Math.round(hrs / 24);
    if (days === 1) return "yesterday";
    if (days < 30) return days + " days ago";
    return new Date(t).toLocaleDateString();
  }

  /**
   * The panel: a column beside the screen, carrying one conversation, its
   * composer, and the history behind it.
   *
   * It is NOT a dialog. No scrim, no aria-modal, no focus trap: the screen it
   * sits next to keeps working, which is the only way a suggestion ("select
   * those 14 keywords and classify them") can be acted on while it is still on
   * screen. The shell gives it its own column, so the content reflows instead
   * of being covered. Closing is the X, or moving to another screen (the shell
   * does that, see main.jsx).
   *
   * Every exchange is saved (POST on the first, PATCH after), so closing the
   * panel, changing screen or reloading the page all leave the conversation
   * where it was. Opening on a module REOPENS that module's most recent chat
   * rather than starting a blank one, because the question someone comes back
   * with is usually the next one in the thread they left.
   *
   * History lists every chat on this site, whichever module it was had on, so
   * a conversation can be found again from anywhere. Opening one from another
   * module moves the whole panel to that module's context: an answer in the
   * Keywords Planner's thread has to be a Keywords Planner answer, wherever it
   * was reopened from.
   */
  function AiAssistPanel({ site, moduleId, onClose, onSaved }) {
    const { Button, IconButton } = DS();
    const siteId = siteIdOf(site);
    const [modId, setModId] = useState(moduleId);   // what the conversation is about
    // The module the conversation is FILED under. It follows a loaded history
    // row, never the user's navigation: a chat that re-filed itself every time
    // they walked to another screen would be findable under none of them.
    const threadMod = useRef(moduleId);
    // The screen the user has walked to while the assistant was busy, waiting
    // to be taken up. Null when the conversation is already pointed there.
    const [offerMove, setOfferMove] = useState(null);
    const [chatId, setChatIdState] = useState(null);  // null = not stored yet
    // Mirrored in a ref because persist() reads it from a closure made before
    // the first save returned: a second question asked while that save is in
    // flight would otherwise start a SECOND row and split the conversation.
    const chatIdRef = useRef(null);
    const setChatId = (id) => { chatIdRef.current = id; setChatIdState(id); };
    const [msgs, setMsgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("chat");       // chat | history
    const [history, setHistory] = useState(null);   // null = not read yet
    const [text, setText] = useState("");
    const [typing, setTyping] = useState(false);
    // A turn that paused for approval. `wire` is the full transcript the
    // server handed back, including the tool blocks: it goes straight back on
    // resume, so the pause survives without the server holding session state.
    const [act, setAct] = useState(null);       // { wire, pending }
    const [autonomous, setAutonomous] = useState(false);
    const [didAct, setDidAct] = useState([]);   // what ran, for the trail under the answer
    const composerRef = useRef(null);
    // What the assistant offered to do next. Carries the real action and its
    // real arguments, so accepting is a click rather than the user rewriting
    // the request in their own words and hoping it lands on the same call.
    const [offers, setOffers] = useState([]);
    // The REAL transcript, as the server last returned it: tool calls, tool
    // results and the blocks that loaded deferred tools, not just the text on
    // screen. Rebuilding the conversation from the visible messages threw all
    // of that away every turn, so the model began each one with no record of
    // what it had read, what it had offered, or which tools it had searched
    // for. Accepting an offer then failed honestly: the tool really was not
    // loaded, and it really had no record of proposing it.
    const convoRef = useRef(null);
    const [saveErr, setSaveErr] = useState(null);
    // The site's state, read once per opening and carried in every message of
    // this conversation. Re-reading it per message would charge the user a
    // dozen aggregate queries to answer one question, and the numbers do not
    // move while they are reading an answer.
    const [context, setContext] = useState(null);   // null = still reading
    const mod = describe(modId);
    const scrollRef = useRef(null);

    // Reopen the module's latest conversation, and read the site. A store that
    // cannot be read is not a reason to refuse the chat: the panel falls back
    // to an unsaved thread, and says so the moment a save fails.
    useEffect(() => {
      let alive = true;
      /* A DIFFERENT SITE IS A DIFFERENT CONVERSATION, and the panel has to say
         so by emptying itself first. Without this the previous site's messages
         stayed on screen whenever the new site had no saved chat (the load
         below simply returned early), and worse, they went back into the next
         request as history: asked about the Site Audit on the blog, the
         assistant answered with the MAIN site's 25 over-long titles, read
         from a thread belonging to another site. Every per-conversation piece
         of state goes, including the model's own transcript. */
      convoRef.current = null;
      setMsgs([]); setChatId(null); setOffers([]); setDidAct([]); setAct(null);
      setOfferMove(null); setSaveErr(null); setContext(null); setLoading(true);
      if (siteId == null) { setLoading(false); setContext(""); return; }
      api(`/assistant/context?site_id=${siteId}`)
        .then(r => { if (alive) setContext((r && r.text) || ""); })
        .catch(() => { if (alive) setContext(""); });
      (async () => {
        try {
          const list = await api(`/assistant/chats?site_id=${siteId}&module=${encodeURIComponent(moduleId || "app")}`);
          const latest = Array.isArray(list) ? list[0] : null;
          if (!latest) return;
          const full = await api(`/assistant/chats/${latest.id}`);
          if (!alive) return;
          setChatId(full.id); setMsgs(full.messages || []);
        } catch { /* nothing stored yet, or the store is unreachable */ }
        finally { if (alive) setLoading(false); }
      })();
      return () => { alive = false; };
      // Deliberately NOT keyed on moduleId. The panel unmounts when closed,
      // so opening it on a screen still loads that screen's latest chat; what
      // changed is that WALKING to another screen no longer throws away the
      // conversation in front of the user.
    }, [siteId]);

    // Walking to another screen is part of the conversation, not the end of
    // it: the assistant is told where the user went and keeps the thread. The
    // exception is when it is mid-task, where quietly re-aiming it under a
    // running turn or a waiting approval card would change what the user is
    // being asked to approve. Then the move is OFFERED instead, and taken up
    // when they say so.
    const busy = typing || !!act;
    const absorbMove = (to) => {
      const next = describe(to);
      setModId(to);
      setOfferMove(null);
      setMsgs(m => [...m, {
        role: "note",
        text: `Moved to ${next.label}`,
        wire: `[The user has moved to the ${next.label} screen. Unless they say otherwise, read what follows as being about it.]`,
      }]);
    };
    useEffect(() => {
      if (moduleId === modId) { setOfferMove(null); return; }
      if (busy) { setOfferMove(moduleId); return; }
      absorbMove(moduleId);
    }, [moduleId, modId, busy]);

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, typing, view]);

    // Written after each exchange, never mid-turn: a half-saved turn would
    // reopen as a question with no answer under it.
    const persist = async (next, forModule) => {
      if (siteId == null) return;
      try {
        const id = chatIdRef.current;
        if (id) {
          await api(`/assistant/chats/${id}`, { method: "PATCH", body: JSON.stringify({ messages: next, transcript: convoRef.current || undefined }) });
        } else {
          const r = await api("/assistant/chats", { method: "POST", body: JSON.stringify({ site_id: siteId, module: forModule, messages: next, transcript: convoRef.current || undefined }) });
          setChatId(r && r.id);
        }
        setSaveErr(null);
        setHistory(null);          // the list is stale now: re-read when opened
        onSaved && onSaved();
      } catch (e) {
        setSaveErr(e.message || "This chat could not be saved.");
      }
    };


    // One agent turn, from either a new question or an answered card.
    const runTurn = async (wire, approvals) => {
      setTyping(true);
      /* A turn that runs for two minutes used to show nothing until it ended.
         Now that it is polled, each poll says what has run so far, so the
         trail fills in step by step and a long turn visibly moves. A poll
         carries the WHOLE list so far, so only the part not already on screen
         is appended, and the answer's own list is folded in the same way: the
         rows must not arrive twice. */
      let shown = 0;
      const onPerformed = (performed) => {
        if (performed.length <= shown) return;
        const fresh = performed.slice(shown);
        shown = performed.length;
        setDidAct(a => [...a, ...fresh]);
      };
      try {
        const res = await actOnce({
          site_id: siteId, messages: wire, autonomous, approvals: approvals || undefined,
          context: systemPrompt(mod, site, context),
          module: mod.id,
        }, onPerformed);
        if (res.performed && res.performed.length > shown) setDidAct(a => [...a, ...res.performed.slice(shown)]);
        setOffers(res.suggestions || []);
        if (res.messages) convoRef.current = res.messages;
        if (res.status === "confirm") { setAct({ wire: res.messages, pending: res.pending }); return null; }
        setAct(null);
        return res.text || "";
      } finally { setTyping(false); }
    };

    // Approve or decline every card at once. Per-action verdicts are the next
    // step; one decision per pause is what the model actually produces today.
    const answerCards = async (verdict) => {
      if (!act) return;
      const approvals = {};
      for (const p of act.pending) approvals[p.id] = verdict;
      const wire = act.wire;
      setAct(null);
      let done = msgs;
      try {
        const text = await runTurn(wire, approvals);
        if (text !== null) done = [...msgs, { role: "ai", text }];
      } catch (e) {
        done = [...msgs, { role: "ai", text: failureText(e), failed: true }];
      }
      if (done !== msgs) { setMsgs(done); persist(done, threadMod.current); }
    };

    const accept = (offer) => {
      setOffers([]);
      send({
        text: offer.label,
        wire: `Do this now: call ${offer.action} with input ${JSON.stringify(offer.input || {})}. I am accepting the suggestion you offered. If that tool is not currently loaded, search for it by name first: it does exist. Do not offer it again.`,
      });
    };

    const send = async (preset) => {
      const asked = typeof preset === "object" && preset ? preset : null;
      const q = (asked ? asked.text : (preset || text)).trim();
      if (!q || typing) return;
      setText(""); setView("chat");
      const next = [...msgs, { role: "user", text: q, ...(asked && asked.wire ? { wire: asked.wire } : {}) }];
      setMsgs(next); setTyping(true);
      let done = next;
      setDidAct([]); setOffers([]);
      try {
        // Continue the real transcript where it left off. Only a thread with
        // no transcript yet (first turn, or one reopened from history) is
        // rebuilt from the visible text, which is the best that can be done
        // for it.
        const wire = convoRef.current
          ? [...convoRef.current, { role: "user", content: asked && asked.wire ? asked.wire : q }]
          : next.map(m => ({
              role   : m.role === "ai" ? "assistant" : "user",
              content: m.wire || m.text,
            }));
        const text = await runTurn(wire);
        // null means the turn is waiting on the user. The question is already
        // on screen and the cards render below it; nothing is appended until
        // they answer, so a pause never looks like an answer.
        if (text === null) return;
        done = [...next, { role: "ai", text }];
      } catch (e) {
        done = [...next, { role: "ai", text: failureText(e), failed: true }];
      }
      setMsgs(done); setTyping(false);
      persist(done, threadMod.current);
    };

    // A new chat does not throw the old one away: it stops writing to it. The
    // previous conversation stays in History, which is the point of storing it.
    // Starting a new chat means the next thing you do is type, so the box
    // takes focus. Without this the click left focus on the button, the first
    // thing typed went to the page behind the drawer, and the list scrolled
    // instead: no error, nothing in the box, and the question had to be typed
    // again. Caught by doing it.
    const newChat = () => {
      convoRef.current = null; setOffers([]); setAct(null); setDidAct([]);
      setChatId(null); setMsgs([]); setText(""); setSaveErr(null); setView("chat");
      requestAnimationFrame(() => composerRef.current && composerRef.current.focus());
    };

    const openHistory = async () => {
      setView("history");
      if (history) return;
      try { setHistory(await api(`/assistant/chats?site_id=${siteId}`)); }
      catch (e) { setHistory([]); setSaveErr(e.message || "Could not read your chats."); }
    };

    const openChat = async (row) => {
      try {
        const full = await api(`/assistant/chats/${row.id}`);
        setChatId(full.id); setMsgs(full.messages || []); setModId(full.module); setView("chat");
        threadMod.current = full.module; setOfferMove(null);
        // Reopened from storage WITH the model's transcript, so the thread
        // carries on knowing what it read, which tools it loaded and what it
        // offered. Null for anything stored before the column existed, and
        // for those the next turn rebuilds from the visible text, which is
        // the best available for them.
        convoRef.current = Array.isArray(full.transcript) && full.transcript.length ? full.transcript : null;
        setOffers([]); setDidAct([]);
      } catch (e) { setSaveErr(e.message || "Could not open that chat."); }
    };

    const deleteChat = async (row) => {
      try {
        await api(`/assistant/chats/${row.id}`, { method: "DELETE" });
        setHistory(h => (h || []).filter(r => r.id !== row.id));
        if (row.id === chatId) newChat();
        onSaved && onSaved();
      } catch (e) { setSaveErr(e.message || "Could not delete that chat."); }
    };

    return (
      /* Escape is bound to the panel, not the window: a non-modal panel that
         swallowed every Escape would close itself when the user dismissed a
         menu on the screen beside it. */
      <aside className="ml-assist" aria-label="AI assistance"
             onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }}>
        <div className="ml-assist__head">
          <div style={{ minWidth: 0 }}>
            <div className="ml-assist__title"><MlIcon name="sparkles" size={16} />AI assistance</div>
            <div className="ml-assist__sub">{mod.label}{site ? " · " + site.name : ""}</div>
          </div>
          <IconButton icon={<MlIcon name="x" size={18} />} label="Close AI assistance" size="sm" onClick={onClose} />
        </div>

        {/* One row, two verbs: what else have I asked (History), and start
            again (New chat). Both belong above the transcript they act on. */}
        <div className="ml-assist__bar">
          <button className={"ml-assist__tab" + (view === "history" ? " is-on" : "")}
                  onClick={() => (view === "history" ? setView("chat") : openHistory())}>
            <MlIcon name={view === "history" ? "arrow-right" : "archive"} size={14} />{view === "history" ? "Back to chat" : "History"}
          </button>
          {view === "chat" && msgs.length > 0 && (
            <button className="ml-assist__tab" onClick={newChat}><MlIcon name="plus" size={14} />New chat</button>
          )}
        </div>

        {view === "history" ? (
          <div className="ml-assist__scroll">
            {history === null ? <div className="bi-subempty">Reading your chats…</div>
              : history.length === 0 ? <div className="bi-subempty">No saved chats on this site yet. Ask something, and it is here next time.</div>
              : (
                <div className="ml-assist__hist">
                  {history.map(row => (
                    <div key={row.id} className={"ml-assist__histrow" + (row.id === chatId ? " is-current" : "")}>
                      <button className="ml-assist__histopen" onClick={() => openChat(row)} title="Open this chat">
                        <span className="ml-assist__histtitle">{row.title}</span>
                        <span className="ml-assist__histmeta">
                          {describe(row.module).label} · {row.message_count} message{row.message_count === 1 ? "" : "s"} · {ago(row.updated_at)}
                        </span>
                      </button>
                      <button className="ml-assist__histdel" onClick={() => deleteChat(row)} aria-label="Delete this chat" title="Delete this chat">
                        <MlIcon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
          </div>
        ) : (
          <div className="ml-assist__scroll" ref={scrollRef}>
            {loading ? <div className="bi-subempty">Opening…</div>
              : msgs.length === 0 ? (
              <div className="ml-assist__empty">
                <span className="ml-msg__avatar ml-msg__avatar--ai" style={{ width: 34, height: 34 }}><MlIcon name="sparkles" size={18} /></span>
                <div className="ml-assist__emptytitle">Ask about {mod.label}, or have it done</div>
                {mod.purpose && <p className="ml-assist__emptysub">{mod.purpose}</p>}
                <div className="ml-chat-suggest">
                  {mod.asks.map(s => <button key={s} onClick={() => send(s)}><MlIcon name="sparkles" size={16} />{s}</button>)}
                  {/* The one question that is the same on every screen, and the
                      one this panel exists to answer. */}
                  <button onClick={() => send("Looking at this site as it stands, what are the three things I should do next, and where?")}>
                    <MlIcon name="compass" size={16} />What should I do next on this site?
                  </button>
                  {/* The openers above ask. This one is the operational half of
                      the same panel, and it is worded so the answer has to be
                      specific to THIS screen and offered as something it can
                      carry out, not another list of advice. The assistant
                      proposes; nothing runs until the offer is accepted. */}
                  <button className="ml-chat-suggest__do"
                          onClick={() => send(`Check the state of ${mod.label} on this site, then offer me the two or three most useful things you could do about it right now. Read before you offer, and do not change anything yet.`)}>
                    <MlIcon name="zap" size={16} />What can you do for me here?
                  </button>
                </div>
              </div>
            ) : (
              <div className="ml-chat">
                {msgs.map((m, i) => m.role === "note" ? (
                  <div className="ml-movednote" key={i}><MlIcon name="compass" size={12} />{m.text}</div>
                ) : m.role === "user" ? (
                  <div className="ml-msg ml-msg--user" key={i}>
                    <span className="ml-msg__avatar ml-msg__avatar--user"><MlIcon name="user" size={15} /></span>
                    <div className="ml-bubble ml-bubble--user">{m.text}</div>
                  </div>
                ) : (
                  <div className="ml-msg" key={i}>
                    <span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span>
                    <div className={"ml-bubble ml-bubble--ai" + (m.failed ? " ml-bubble--failed" : "")}><Linked text={m.text} /></div>
                  </div>
                ))}
                {didAct.length > 0 && (
                  <div className="ml-acted">
                    {didAct.map((a, i) => (
                      <div className="ml-acted__row" key={i}>
                        <MlIcon name={a.declined ? "x" : a.ok === false ? "alert-triangle" : "check"} size={12} />
                        {/* An HTTP status is ours, not theirs. Live, a step
                            that failed for a reason it had already explained
                            in words ("none of these pages could be matched to
                            a post on this site") rendered as "(failed, 200)":
                            a code that says OK, next to the word failed, on a
                            line that had already said why. The word is the
                            whole of what a reader can use. */}
                        <span>{a.summary}{a.declined ? " (declined)" : a.ok === false ? " (failed)" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* One to three next steps, every turn, as small text rather
                    than a stack of cards: see .ml-offers. The lede says they
                    are offers and not things already underway, which a line
                    cannot say for itself the way a bordered panel could. */}
                {offers.length > 0 && !act && (
                  <div className="ml-offers">
                    <span className="ml-offers__lede">
                      {offers.length === 1 ? "Next, if you want it:" : "Next, if you want them:"}
                    </span>
                    {offers.map((o, i) => (
                      <button type="button" key={i}
                              className={"ml-offer" + (o.effect === "destructive" ? " ml-offer--danger" : "")}
                              title={o.why ? `${o.label} - ${o.why}` : o.label}
                              onClick={() => accept(o)}>
                        <MlIcon name="zap" size={14} />
                        <span className="ml-offer__text">
                          <span className="ml-offer__label">{o.label}</span>
                          {o.why && <span className="ml-offer__why">{o.why}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {offerMove && (
                  <div className="ml-movehere">
                    <span>You are on {describe(offerMove).label} now.</span>
                    <Button size="sm" onClick={() => absorbMove(offerMove)}>Bring the assistant here</Button>
                  </div>
                )}
                {act && (
                  <div className="ml-approve">
                    <div className="ml-approve__title">
                      <MlIcon name="alert-triangle" size={14} />
                      {act.pending.length === 1 ? "This will change your data" : `These ${act.pending.length} actions will change your data`}
                    </div>
                    {act.pending.map(p => (
                      <div className={"ml-approve__row" + (p.effect === "destructive" ? " ml-approve__row--danger" : "")} key={p.id}>
                        {p.summary}
                      </div>
                    ))}
                    <div className="ml-approve__actions">
                      <Button variant="primary" size="sm" onClick={() => answerCards("allow")}>Approve</Button>
                      <Button size="sm" onClick={() => answerCards("deny")}>Decline</Button>
                    </div>
                  </div>
                )}
                {typing && (
                  <div className="ml-msg">
                    <span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span>
                    <div className="ml-bubble ml-bubble--ai"><span className="ml-typing"><span></span><span></span><span></span></span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="ml-assist__foot">
          <div className="ml-composer">
            <textarea ref={composerRef} rows={1} placeholder={`Ask about ${mod.label}…`} value={text}
                      onChange={e => setText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button className="ml-composer__send" variant="primary" size="sm" aria-label="Send" onClick={() => send()}
                    leadingIcon={<MlIcon name="send" size={16} />} />
          </div>
          <div className="ml-assist__ctx">
            <span className="ml-chip ml-chip--selected" style={{ height: 24 }}><MlIcon name="compass" size={13} />{mod.label}</span>
            {/* Both modes on screen, with the live one filled in. A single
                toggle showing one label reads as the action the button
                performs ("click to Ask first"), which implies the OTHER mode
                is the one currently running: the opposite of the truth, on
                the one control where being sure matters most. */}
            <div className="ml-autonomy" role="group" aria-label="What the assistant may do without asking">
              <button type="button"
                      className={"ml-autonomy__opt" + (autonomous ? "" : " is-on")}
                      aria-pressed={!autonomous}
                      title="Nothing is created, changed or deleted until you approve it."
                      onClick={() => setAutonomous(false)}>
                <MlIcon name="shield-check" size={13} />Ask first
              </button>
              <button type="button"
                      className={"ml-autonomy__opt ml-autonomy__opt--auto" + (autonomous ? " is-on" : "")}
                      aria-pressed={autonomous}
                      title="Changes are made without asking. The list under each answer still says what was done."
                      onClick={() => setAutonomous(true)}>
                <MlIcon name="zap" size={13} />Autonomous
              </button>
            </div>
            {chatId && !saveErr && <span className="ml-assist__saved"><MlIcon name="check" size={12} />Saved</span>}
          </div>
          {/* What it can see, said where it is read. "Reading this site" rather
              than a claim of omniscience: it has the summary, not the posts. */}
          <p className="ml-assist__note">
            {context === null ? "Reading this site…"
              : context ? "Knows this site's profile, banks, plans, competitors, audit and search data."
              : "Answering without this site's data: the summary could not be read."}
          </p>
          {saveErr && <p className="ml-assist__note ml-assist__note--bad">{saveErr}</p>}
        </div>
      </aside>
    );
  }

  /**
   * The topbar trigger. Controlled by the shell, which owns the open state
   * because the panel is a column of the shell's layout and because moving to
   * another screen has to close it.
   *
   * The dot means "there is a saved conversation about this screen", read from
   * the store rather than from this session, so it is still there tomorrow.
   * Re-read whenever the panel closes and when the site changes, which is every
   * moment it can have changed.
   */
  function AiAssistTrigger({ site, moduleId, open, onToggle }) {
    const { IconButton } = DS();
    const [modsWithChats, setMods] = useState(() => new Set());
    const siteId = siteIdOf(site);
    const here = moduleId || "app";

    const readIndex = React.useCallback(() => {
      if (siteId == null) return;
      api(`/assistant/chats?site_id=${siteId}`, { silent401: true })
        .then(rows => setMods(new Set((rows || []).map(r => r.module))))
        .catch(() => { /* the dot is a hint, never an error */ });
    }, [siteId]);
    useEffect(() => { if (!open) readIndex(); }, [readIndex, open]);

    return (
      <IconButton
        className={"ml-aiassist" + (open ? " ml-aiassist--on" : "") + (modsWithChats.has(here) ? " ml-aiassist--thread" : "")}
        bordered size="sm"
        label={open ? "Close AI assistance" : `AI assistance for ${describe(here).label}`}
        icon={<MlIcon name="sparkles" size={18} />}
        aria-expanded={!!open}
        onClick={onToggle}
      />
    );
  }

  window.MlAiDrawer = { AiAssistTrigger, AiAssistPanel, MODULE_CTX };
})();
